import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, X, Maximize2, Minimize2, ShoppingCart, ArrowLeft,
  Search, User, IndianRupee, Trash2, Package
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const MAX_SCREENS = 4;

const POSMultiScreenPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [screens, setScreens] = useState([
    { id: 1, name: 'Counter 1', cart: [], customer: { name: 'Walk-in Customer' }, active: true }
  ]);
  const [activeScreenId, setActiveScreenId] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const bid = businessId || activeBusiness?.id;

  const activeScreen = screens.find(s => s.id === activeScreenId) || screens[0];

  const addScreen = () => {
    if (screens.length >= MAX_SCREENS) {
      toast.error(`Maximum ${MAX_SCREENS} screens allowed`);
      return;
    }
    const newId = Math.max(...screens.map(s => s.id)) + 1;
    setScreens([...screens, {
      id: newId,
      name: `Counter ${newId}`,
      cart: [],
      customer: { name: 'Walk-in Customer' },
      active: true
    }]);
    setActiveScreenId(newId);
  };

  const doRemoveScreen = (screenId) => {
    const newScreens = screens.filter(s => s.id !== screenId);
    setScreens(newScreens);
    if (activeScreenId === screenId) {
      setActiveScreenId(newScreens[0].id);
    }
  };

  const removeScreen = (screenId) => {
    if (screens.length === 1) {
      toast.error('Cannot remove last screen');
      return;
    }
    const screen = screens.find(s => s.id === screenId);
    if (screen?.cart?.length > 0) {
      setConfirmModal({
        title: 'Remove Counter',
        description: `${screen.name} has ${screen.cart.length} items in the cart. Remove anyway?`,
        confirmLabel: 'Remove',
        confirmClass: 'bg-red-600 hover:bg-red-700',
        onConfirm: () => { setConfirmModal(null); doRemoveScreen(screenId); }
      });
      return;
    }
    doRemoveScreen(screenId);
  };

  const updateScreen = (screenId, updates) => {
    setScreens(screens.map(s => s.id === screenId ? { ...s, ...updates } : s));
  };

  const searchItems = useCallback(async (query) => {
    if (!query || query.length < 1) { setSearchResults([]); return; }
    try {
      const { data } = await api.get('/pos/items/search', { params: { q: query } });
      setSearchResults(data.items || []);
      setShowResults(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error searching items'));
    }
  }, [bid]);

  useEffect(() => {
    const timer = setTimeout(() => searchItems(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchItems]);

  const addToCart = (item) => {
    const cart = activeScreen.cart;
    const existingIndex = cart.findIndex(i => i.item_id === item.id);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].amount = newCart[existingIndex].quantity * newCart[existingIndex].price;
      newCart[existingIndex].tax_amount = newCart[existingIndex].amount * (newCart[existingIndex].tax_rate / 100);
      updateScreen(activeScreenId, { cart: newCart });
    } else {
      const price = item.selling_price || item.sale_price || item.price || 0;
      const taxRate = item.tax_rate || item.gst_rate || 18;
      updateScreen(activeScreenId, {
        cart: [...cart, {
          item_id: item.id,
          item_name: item.name,
          item_code: item.sku || item.item_code || item.code,
          quantity: 1,
          unit: item.unit || 'PCS',
          price,
          discount: 0,
          discount_percent: 0,
          tax_rate: taxRate,
          tax_amount: price * (taxRate / 100),
          amount: price
        }]
      });
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const removeFromCart = (index) => {
    const newCart = activeScreen.cart.filter((_, i) => i !== index);
    updateScreen(activeScreenId, { cart: newCart });
  };

  const updateQuantity = (index, qty) => {
    if (qty < 1) return;
    const newCart = [...activeScreen.cart];
    newCart[index].quantity = qty;
    newCart[index].amount = qty * newCart[index].price;
    newCart[index].tax_amount = newCart[index].amount * (newCart[index].tax_rate / 100);
    updateScreen(activeScreenId, { cart: newCart });
  };

  const handleSave = async () => {
    const cart = activeScreen.cart;
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    
    const subtotal = cart.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = cart.reduce((sum, item) => sum + item.tax_amount, 0);
    const grandTotal = Math.round(subtotal + taxAmount);
    
    try {
      const payload = {
        customer_name: activeScreen.customer.name,
        items: cart,
        subtotal,
        discount_amount: 0,
        tax_amount: taxAmount,
        additional_charges: 0,
        round_off: grandTotal - (subtotal + taxAmount),
        grand_total: grandTotal,
        payments: [{ mode: 'CASH', amount: grandTotal }],
        total_paid: grandTotal
      };

      const { data } = await api.post('/pos/invoice', payload);
      toast.success(`Invoice ${data.invoice?.invoice_number} created on ${activeScreen.name}`);
      updateScreen(activeScreenId, { cart: [], customer: { name: 'Walk-in Customer' } });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error creating invoice'));
    }
  };

  const subtotal = activeScreen.cart.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = activeScreen.cart.reduce((sum, item) => sum + item.tax_amount, 0);
  const grandTotal = Math.round(subtotal + taxAmount);

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/business/${bid}/pos`)} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-bold text-slate-800">Multi-Counter POS</h1>
          </div>
          
          {/* Screen Tabs */}
          <div className="flex items-center gap-2">
            {screens.map(screen => (
              <button
                key={screen.id}
                onClick={() => setActiveScreenId(screen.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeScreenId === screen.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <ShoppingCart className="w-4 h-4" />
                {screen.name}
                {screen.cart.length > 0 && (
                  <span className={`px-1.5 py-0.5 text-xs rounded-full ${activeScreenId === screen.id ? 'bg-blue-500' : 'bg-slate-300'}`}>
                    {screen.cart.length}
                  </span>
                )}
                {screens.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeScreen(screen.id); }}
                    className="ml-1 p-0.5 hover:bg-white/20 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </button>
            ))}
            {screens.length < MAX_SCREENS && (
              <button
                onClick={addScreen}
                className="p-2 bg-emerald-100 text-emerald-600 rounded-lg hover:bg-emerald-200"
              >
                <Plus className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Items */}
        <div className="flex-1 p-4 overflow-auto">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items by name, code or barcode..."
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm bg-white"
            />
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 max-h-64 overflow-auto">
                {searchResults.map(item => (
                  <button
                    key={item.id}
                    onClick={() => addToCart(item)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.sku || item.item_code || item.code}</p>
                    </div>
                    <span className="font-semibold text-blue-600">₹{item.selling_price || item.price}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cart */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{activeScreen.name} - Cart</h3>
              <span className="text-sm text-slate-500">{activeScreen.cart.length} items</span>
            </div>
            
            {activeScreen.cart.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Package className="w-12 h-12 mx-auto mb-2" />
                <p>Cart is empty</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {activeScreen.cart.map((item, index) => (
                  <div key={index} className="p-3 flex items-center gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.item_name}</p>
                      <p className="text-xs text-slate-500">₹{item.price} x {item.quantity}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        className="w-7 h-7 bg-slate-100 rounded flex items-center justify-center"
                      >-</button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="w-7 h-7 bg-slate-100 rounded flex items-center justify-center"
                      >+</button>
                    </div>
                    <p className="font-semibold text-slate-800 w-20 text-right">₹{item.amount}</p>
                    <button onClick={() => removeFromCart(index)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Summary */}
        <div className="w-80 bg-white border-l border-slate-200 p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600">{activeScreen.customer.name}</span>
          </div>
          
          <div className="flex-1" />
          
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Subtotal</span>
              <span className="text-slate-800">₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Tax</span>
              <span className="text-slate-800">₹{taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span className="text-slate-800">Total</span>
              <span className="text-blue-600">₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={activeScreen.cart.length === 0}
            className="mt-4 w-full py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Complete Sale
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default POSMultiScreenPage;
